import { readJsonConfig } from "./helpers/config_helpers";
import { loadProductSection, shopifyFiltersToAlgoliaFilters, DIGITAL_INDEX_ATTRIBUTE_OVERRIDES, batchDelay } from "./helpers/shopify_helper";
import { AlgoliaClient } from "./algolia";
import { onQueryChange } from "./helpers/querystring_watcher";

declare global {
  interface Window {
    __algoliaLoaderInit?: boolean;
    lastAlgoliaResponse?: any;
  }
}

interface SearchConfig {
	apiKey: string;
	appId: string;
	index: string;
	collectionHandle: string;
	collectionId?: string | number;
	ruleContexts?: string[];
	containerId: string;
	sectionId: string;
	moreText: string;
	size: number;
	sortIndices?: Record<string, string>;
}

type ProductHit = { handle?: string; objectID: string };

export function chunk<T>(arr: T[], size: number): T[][] {
	if (size <= 0) throw new Error("chunk size must be > 0");
	const out: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		out.push(arr.slice(i, i + size));
	}
	return out;
}

console.log("Algolia loader script");

function init() {
	if (window.__algoliaLoaderInit) {
		console.log("[Algolia] init skipped: already initialized");
		return;
	}
	window.__algoliaLoaderInit = true;

	const config = readJsonConfig<SearchConfig>("algolia");
	let first = true;
	const renderedHandles = new Set<string>();

	/**
	 * Contatore di versione per le ricerche: ogni nuova richiesta incrementa
	 * searchVersion. La ricerca in corso confronta il proprio valore con quello
	 * corrente per capire se è stata superata da una più recente e deve fermarsi.
	 */
	let searchVersion = 0;
	let loading = false;

	const client = new AlgoliaClient(config?.apiKey || "", config?.appId || "", config?.index || "");

	/**
	 * True se l'indice configurato è la custom integration (_digital).
	 * I mercati con indice legacy (es. shopify_products_fr_fr) non usano
	 * DIGITAL_INDEX_ATTRIBUTE_OVERRIDES né l'estrazione del product handle.
	 */
	const isDigitalIndex = (config?.index ?? "").includes("_digital");

	/**
	 * Legge sort_by dall'URL e switcha l'indice Algolia sulla replica corrispondente.
	 * Se non c'è mapping o sort_by non è presente, usa l'indice di default dal config.
	 */
	function resolveAndSetIndex(): void {
		if (!config?.sortIndices) return;
		const params = new URLSearchParams(location.search);
		const sortBy = params.get('sort_by') || 'manual';
		const targetIndex = config.sortIndices[sortBy] || config.index || '';
		if (targetIndex && targetIndex !== client.getIndex()) {
			console.log('[Algolia] switching index for sort:', { sortBy, from: client.getIndex(), to: targetIndex });
			client.setIndex(targetIndex);
		}
	}

	/**
	 * Handle della collection, potenzialmente aggiornato dall'handle discovery.
	 * Shopify traduce gli handle per locale (es. donna-saldi → femme-soldes) ma le
	 * regole Algolia sono configurate sull'handle originale italiano. Scopriamo
	 * l'handle originale dall'indice Algolia prima della prima ricerca.
	 */
	const plpCollectionHandle: string = config?.collectionHandle || "";
	let algoliaCollectionHandle: string = plpCollectionHandle;
	// Discovery: rimappa l'handle localizzato (es. tricots-pour-femmes) all'handle
	// originale italiano presente nell'indice (es. maglieria-donna) usando collection_ids.
	// Richiede che collection_ids sia filtrabile sull'indice (vale anche per _digital).
	const handleDiscovery: Promise<void> = (config?.collectionId && algoliaCollectionHandle)
		? client.discoverHandleFromId(config.collectionId).then(original => {
			if (original && original !== algoliaCollectionHandle) {
				console.log("[Algolia] handle remapped for rules:", algoliaCollectionHandle, "→", original);
				algoliaCollectionHandle = original;
			}
		}).catch(() => {})
		: Promise.resolve();

	/** Converte i parametri URL nei due formati di filtri (digital + legacy). */
	function buildFilters(search: string) {
		const params = new URLSearchParams(search);
		const digitalFilters = shopifyFiltersToAlgoliaFilters(params, {
			stripMetaPrefix: true,
			attributeOverrides: DIGITAL_INDEX_ATTRIBUTE_OVERRIDES
		});
		const legacyFilters = shopifyFiltersToAlgoliaFilters(params);
		return { digitalFilters, legacyFilters };
	}

	function setLoadMoreActive(active: boolean): void {
		const el = document.getElementById("load-more");
		if (el) el.classList.toggle("active", active);
	}

	async function performSearch(page: number, digitalFilters: string[] = [], legacyFilters: string[] = []) {
		const myVersion = ++searchVersion;

		if (loading) {
			console.log("[Algolia] search superseded, a newer request will run", { myVersion });
		}

		while (loading) {
			await new Promise(r => setTimeout(r, 50));
			if (myVersion !== searchVersion) {
				console.log("[Algolia] search cancelled (superseded while waiting)", { myVersion, searchVersion });
				return;
			}
		}

		loading = true;
		if (page === 0) setLoadMoreActive(false);

		try {
			if (!config?.containerId) {
				console.warn("Container ID not found");
				return;
			}
			if (!config?.sectionId) {
				console.warn("Section ID not found");
				return;
			}

			const container = document.getElementById(config.containerId);
			if (!container) {
				console.warn("Container not found");
				return;
			}

			if (window.lastAlgoliaResponse && window.lastAlgoliaResponse.hits.length === 0) {
				container.innerHTML = "";
			}

			await handleDiscovery;

			if (myVersion !== searchVersion) {
				console.log("[Algolia] search cancelled after handle discovery", { myVersion, searchVersion });
				return;
			}

			const ruleContexts = (config?.ruleContexts && config.ruleContexts.length > 0)
				? config.ruleContexts
				: (algoliaCollectionHandle
					? [algoliaCollectionHandle, `collection:${algoliaCollectionHandle}`]
					: []);

			const filters = isDigitalIndex ? [...digitalFilters, "status:ACTIVE"] : legacyFilters;
			const effectiveCollectionId = config?.collectionId;
			console.log("[Algolia] search", { page, filters, ruleContexts, isDigitalIndex, version: myVersion });
			const baseSize = config?.size || 24;
			const hitsPerPage = Math.min(baseSize * 3, 72);
			const response = await client.search<ProductHit>(
				algoliaCollectionHandle,
				hitsPerPage,
				page,
				filters,
				effectiveCollectionId,
				ruleContexts,
				{ skipHandleDiscovery: effectiveCollectionId != null }
			);

			if (myVersion !== searchVersion) {
				console.log("[Algolia] search cancelled after Algolia response", { myVersion, searchVersion });
				return;
			}

			window.lastAlgoliaResponse = response;
			// Il contatore in pagina viene aggiornato da main-collection `onAlgoliaSearchComplete` (nbHits).
			window.dispatchEvent(new CustomEvent("algolia-search-complete", { detail: response }));

			if (response.appliedRules && response.appliedRules.length > 0) {
				console.log("[Algolia] applied rules", response.appliedRules);
			}

			if (response.hits.length === 0) {
				setLoadMoreActive(false);
				const template = document.querySelector("#algolia-no-results-template") as HTMLTemplateElement;
				if (!template) {
					container.innerHTML = `<div class="col-12 text-center py-5">Nessun prodotto trovato</div>`;
					return;
				}
				container.innerHTML = "";
				container.appendChild(template.content.cloneNode(true));
				return;
			}

			const sectionParams: Record<string, string | boolean> = {};
			sectionParams.show_video_plp = "1";
			if (plpCollectionHandle) sectionParams.collection_handle = plpCollectionHandle;
			const sectionParamsArg = Object.keys(sectionParams).length > 0 ? sectionParams : undefined;
			const missingHandleHits = response.hits.filter(
				(hit) => typeof hit?.handle !== "string" || hit.handle.trim().length === 0
			);
			if (missingHandleHits.length > 0) {
				console.warn("[Algolia] hits missing handle", missingHandleHits.map((hit) => hit.objectID));
			}

			const hitsWithHandle = response.hits
				.map((hit) => ({
					...hit,
					handle: typeof hit.handle === "string" ? hit.handle.trim() : ""
				}))
				.filter((hit) => hit.handle.length > 0);

			// Dedupe difensivo su tutta la pagina di risultati.
			const seenInPage = new Set<string>();
			const uniqueHits = hitsWithHandle.filter((hit) => {
				const handle = hit.handle as string;
				if (seenInPage.has(handle)) return false;
				if (renderedHandles.has(handle)) return false;
				seenInPage.add(handle);
				return true;
			});

			if (first) {
				container.innerHTML = "";
				first = false;
			}
			if (myVersion !== searchVersion) {
				console.log("[Algolia] search cancelled during rendering", { myVersion, searchVersion });
				return;
			}

			const BATCH_SIZE = 2;
			const INTER_BATCH_DELAY = 150;

			for (let i = 0; i < uniqueHits.length; i += BATCH_SIZE) {
				if (myVersion !== searchVersion) {
					console.log("[Algolia] search cancelled during rendering", { myVersion, searchVersion });
					return;
				}

				if (i > 0) await batchDelay(INTER_BATCH_DELAY);

				const batch = uniqueHits.slice(i, i + BATCH_SIZE);
				const batchResults = await Promise.all(
					batch.map(async (hit) => {
						try {
							const section = await loadProductSection(
								config!.sectionId,
								hit.handle as string,
								sectionParamsArg,
								plpCollectionHandle || undefined
							);

							if (myVersion !== searchVersion) return null;

							const doc = new DOMParser().parseFromString(section, "text/html");
							const el = doc.querySelector(".shopify-section");
							if (!el) {
								console.warn("[Algolia] product section missing .shopify-section", {
									objectID: hit.objectID,
									handle: hit.handle
								});
								return null;
							}

							const productCard = el.querySelector('product-card');
							if (!productCard || !productCard.getAttribute('data-product-id')) return null;

							renderedHandles.add(hit.handle as string);
							el.removeAttribute("id");
							return el;
						} catch (error) {
							const message = error instanceof Error ? error.message : String(error);
							console.warn(
								"[Algolia] product section load failed",
								`Algolia product section load failed for ${hit.objectID} (${hit.handle}): ${message}`
							);
							return null;
						}
					})
				);

				const frag = document.createDocumentFragment();
				for (const el of batchResults) {
					if (el) frag.appendChild(el);
				}
				container.appendChild(frag);
			}

			const hasMorePages = (response.page ?? 0) + 1 < (response.nbPages ?? 0);
			setLoadMoreActive(hasMorePages);

			// Corregge il contatore prodotti in base a quanti sono stati effettivamente
			// renderizzati (esclude prodotti non pubblicati nel mercato corrente).
			const correctedCount = hasMorePages
				? Math.max(1, Math.round(response.nbHits * (renderedHandles.size / Math.max(1, (response.page ?? 0) * hitsPerPage + uniqueHits.length))))
				: renderedHandles.size;
			window.dispatchEvent(new CustomEvent("algolia-render-count", {
				detail: { count: correctedCount }
			}));
		} catch (error) {
			console.error("[Algolia] search failed", error);
		} finally {
			loading = false;
		}
	}

	// Filtri iniziali calcolati dai parametri URL
	let currentFilters = buildFilters(location.search);

	window.addEventListener("algolia-load-more", () => {
		if (!window.lastAlgoliaResponse || loading) return;
		resolveAndSetIndex();
		performSearch(
			(window.lastAlgoliaResponse.page ?? 0) + 1,
			currentFilters.digitalFilters,
			currentFilters.legacyFilters
		);
	});

	onQueryChange(({ oldSearch, newSearch, url }) => {
		console.log("[Algolia] query changed", { oldSearch, newSearch, url });
		currentFilters = buildFilters(newSearch);
		resolveAndSetIndex();
		first = true;
		renderedHandles.clear();
		performSearch(0, currentFilters.digitalFilters, currentFilters.legacyFilters);
	});

	// Canale diretto per il cambio di ordinamento: bypassa la rilevazione del cambio URL
	// per garantire che il sort funzioni anche quando onQueryChange non si attiva
	window.addEventListener("algolia-sort-changed", (e) => {
		const sortValue = (e as CustomEvent).detail?.sortValue as string | undefined;
		if (!sortValue) return;
		console.log("[Algolia] direct sort change:", sortValue);
		currentFilters = buildFilters(location.search);
		resolveAndSetIndex();
		first = true;
		renderedHandles.clear();
		performSearch(0, currentFilters.digitalFilters, currentFilters.legacyFilters);
	});

	resolveAndSetIndex();
	console.log("[Algolia] initial search", currentFilters);
	renderedHandles.clear();
	performSearch(0, currentFilters.digitalFilters, currentFilters.legacyFilters);

	// IntersectionObserver per infinite scroll: quando il load-more diventa visibile
	// (.active), dispatcha l'evento che trigghera il caricamento della pagina successiva.
	const loadMoreEl = document.getElementById("load-more");
	if (loadMoreEl) {
		new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && !loading) {
				window.dispatchEvent(new Event("algolia-load-more"));
			}
		}, { rootMargin: "400px 0px 400px 0px" }).observe(loadMoreEl);
	}
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => init(), { once: true });
} else {
  init();
}
